import { LoadingSpinner, useModalState } from "@clipboard-health/ui-components";
import { getTheme } from "@clipboard-health/ui-theme";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, ThemeProvider } from "@mui/material";
import { type ClipboardScoreLog } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreHistory";
import { PostShiftReviewFlow } from "@src/appV2/PostShiftReview/PostShiftReviewFlow";
import { EmergencyCreditsEducationContainer } from "@src/appV2/redesign/ClipboardScore/components/EmergencyCreditsEducation/EmergencyCreditsEducationContainer";
import { useEmergencyCreditsConfig } from "@src/appV2/redesign/EmergencyCredits/hooks/useEmergencyCreditsConfig";
import { type ReactNode, useCallback } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";

import { getCreditProgressBarLabel } from "../../../../EmergencyCredits/utils/getCreditProgressBarLabel";
import { MY_SHIFTS_FULL_PATH } from "../../../path";
import { useECClockOutFlow } from "./hooks/useECClockOutFlow";
import { useEmergencyCreditsClockOutData } from "./hooks/useEmergencyCreditsClockOutData";
import { YourAccountIsRestrictedPage } from "./YourAccountIsRestricted/YourAccountIsRestrictedPage";
import { YourClipboardScorePage } from "./YourClipboardScore/YourClipboardScorePage";
import { YourEmergencyCreditsPage } from "./YourEmergencyCredits/YourEmergencyCreditsPage";

interface RouteParams {
  shiftId: string;
}

interface LocationState {
  facility?: {
    id: string;
    name: string;
    type: string;
  };
  isDelayedWorkplaceReviewEnabled?: boolean;
  backgroundGradient?: string;
  scoreLogs?: ClipboardScoreLog[];
  currentScore?: number;
  scoreChange?: number;
  latenessMinutes: number;
  isSecondShiftInDouble?: boolean;
  isFirstShiftInDouble?: boolean;
  wasBookedWithShortLeadTime?: boolean;
  newlyRestricted?: boolean;
  isArrivalTimeFeatureEnabled?: boolean;
  formattedTargetArrivalTime?: string;
}

export function EmergencyCreditsClockOutFlowPage() {
  const { shiftId } = useParams<RouteParams>();
  const history = useHistory();
  const location = useLocation<LocationState>();
  const postShiftReviewModalState = useModalState();
  const educationModalState = useModalState();
  const emergencyCreditsConfig = useEmergencyCreditsConfig();

  const {
    facility,
    isDelayedWorkplaceReviewEnabled,
    backgroundGradient,
    scoreLogs,
    currentScore,
    scoreChange,
    latenessMinutes,
    isSecondShiftInDouble,
    isFirstShiftInDouble,
    wasBookedWithShortLeadTime,
    newlyRestricted,
    isArrivalTimeFeatureEnabled,
    formattedTargetArrivalTime,
  } = location.state ?? {};

  const {
    emergencyCreditInfo,
    clipboardScoreData,
    restrictionData: { liftTime, reEnrollmentScore, isFinalRestriction },
    isProfileLoading,
  } = useEmergencyCreditsClockOutData({
    scoreLogs,
    currentScore,
    scoreChange,
    latenessMinutes,
    isSecondShiftInDouble: isSecondShiftInDouble ?? false,
    isFirstShiftInDouble: isFirstShiftInDouble ?? false,
    wasBookedWithShortLeadTime: wasBookedWithShortLeadTime ?? false,
    newlyRestricted: newlyRestricted ?? false,
    isArrivalTimeFeatureEnabled: isArrivalTimeFeatureEnabled ?? false,
    formattedTargetArrivalTime,
  });

  const exitFlow = useCallback((): void => {
    if ((isDelayedWorkplaceReviewEnabled ?? false) || !facility) {
      history.replace(MY_SHIFTS_FULL_PATH);
    } else {
      postShiftReviewModalState.openModal();
    }
  }, [history, isDelayedWorkplaceReviewEnabled, facility, postShiftReviewModalState]);

  const flow = useECClockOutFlow({
    areBothCreditsMaxed: emergencyCreditInfo?.areBothCreditsMaxed ?? false,
    gainedCancellationCredit: emergencyCreditInfo?.gainedCancellationCredit ?? false,
    gainedLatenessCredit: emergencyCreditInfo?.gainedLatenessCredit ?? false,
    usedLatenessCredit: emergencyCreditInfo?.usedLatenessCredit ?? false,
    cancellationCreditStats: emergencyCreditInfo?.cancellationCreditStats ?? {
      totalAvailableCredits: 0,
      creditCap: 0,
    },
    latenessCreditStats: emergencyCreditInfo?.latenessCreditStats ?? {
      totalAvailableCredits: 0,
      creditCap: 0,
    },
    newlyRestricted: newlyRestricted === true,
    hasReEnrollmentScore: isDefined(reEnrollmentScore),
    onExit: exitFlow,
  });

  const handlePostShiftReviewComplete = useCallback((): void => {
    postShiftReviewModalState.closeModal();
    history.replace(MY_SHIFTS_FULL_PATH);
  }, [history, postShiftReviewModalState]);

  if (isProfileLoading) {
    return (
      <ThemeProvider theme={getTheme()}>
        <Box
          role="progressbar"
          aria-label="Loading emergency credits"
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100vh"
        >
          <LoadingSpinner size="medium" />
        </Box>
      </ThemeProvider>
    );
  }

  if (!isDefined(emergencyCreditInfo)) {
    history.replace(MY_SHIFTS_FULL_PATH);
    return null;
  }

  const {
    cancellationCreditStats,
    latenessCreditStats,
    canUseCredit,
    hasAvailableLatenessCredit,
    gainedCancellationCredit,
    gainedLatenessCredit,
    usedLatenessCredit,
  } = emergencyCreditInfo;

  const isWorkerLate = isDefined(latenessMinutes) && latenessMinutes > 0;

  let stepContent: ReactNode;

  if (flow.currentStep === "restricted") {
    stepContent = (
      <YourAccountIsRestrictedPage
        isFinalRestriction={isFinalRestriction}
        liftTime={liftTime}
        reEnrollmentScore={reEnrollmentScore!}
        onContinue={flow.handleRestrictedContinue}
      />
    );
  } else if (flow.currentStep === "clipboardScore") {
    if (!isDefined(clipboardScoreData)) {
      history.replace(MY_SHIFTS_FULL_PATH);
      return null;
    }

    stepContent = (
      <YourClipboardScorePage
        showCreditProgressBar={isWorkerLate}
        startScore={clipboardScoreData.startScore}
        scoreChanges={clipboardScoreData.scoreChanges}
        subtitle={clipboardScoreData.subtitle}
        doubleShiftNote={clipboardScoreData.doubleShiftNote}
        gradientVariant={clipboardScoreData.gradientVariant}
        creditProgressBarProps={{
          completedCount: usedLatenessCredit
            ? latenessCreditStats.requiredShiftsForNextCredit
            : emergencyCreditInfo.completedShiftsToDisplayForLatenessCredit,
          requiredCount: latenessCreditStats.requiredShiftsForNextCredit,
          label: getCreditProgressBarLabel(
            "lateness",
            hasAvailableLatenessCredit || usedLatenessCredit
          ),
          color: "green",
          mode: "static",
        }}
        creditAmount={clipboardScoreData.savedPoints}
        canUseCredit={canUseCredit}
        autoApplyCredit={newlyRestricted === true && canUseCredit}
        onContinue={flow.handleClipboardScoreContinue}
      />
    );
  } else {
    stepContent = (
      <YourEmergencyCreditsPage
        backgroundGradient={backgroundGradient}
        cancellationStats={cancellationCreditStats}
        latenessStats={latenessCreditStats}
        gainedCancellationCredit={gainedCancellationCredit}
        gainedLatenessCredit={gainedLatenessCredit}
        usedLatenessCredit={usedLatenessCredit}
        onContinue={flow.handleEmergencyCreditsContinue}
        onLearnMoreClick={educationModalState.openModal}
      />
    );
  }

  return (
    <ThemeProvider theme={getTheme()}>
      {stepContent}
      {emergencyCreditsConfig.enabled && (
        <EmergencyCreditsEducationContainer
          showCloseButton
          modalState={educationModalState}
          markAsSeen={() => {}}
          emergencyCreditsConfig={emergencyCreditsConfig}
        />
      )}
      {facility && (
        <PostShiftReviewFlow
          modalState={postShiftReviewModalState}
          facility={{
            id: facility.id,
            name: facility.name,
            type: facility.type,
            photo: undefined,
          }}
          shiftId={shiftId}
          onComplete={handlePostShiftReviewComplete}
        />
      )}
    </ThemeProvider>
  );
}
